import React, { useMemo, useState } from "react";
import { Tag as TagIcon, Check, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AccountTagText, AccountTagInline } from "@/components/tags/AccountTag";

/**
 * Multi-select for assigning account tags to a user.
 * `allTags` = full catalog (active + inactive). `value` = array of tag IDs.
 * onChange(nextIds) fires on every toggle (caller persists).
 * Inactive tags are only shown if already assigned (greyed) so they can be removed.
 */
export function TagPicker({ allTags = [], value = [], onChange, testId = "tag-picker", compact = false, max }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const byId = useMemo(() => Object.fromEntries(allTags.map((t) => [t.id, t])), [allTags]);
  const selected = useMemo(() => value.map((id) => byId[id]).filter(Boolean), [value, byId]);

  const options = useMemo(() => {
    const set = new Set(value);
    const list = allTags.filter((t) => t.active || set.has(t.id));
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((t) => (t.name || "").toLowerCase().includes(needle)) : list;
  }, [allTags, value, q]);

  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-2.5 py-1.5 text-left hover:bg-secondary/40 ${compact ? "h-8" : ""}`}
          data-testid={`${testId}-trigger`}
        >
          {selected.length > 0 ? (
            <AccountTagInline tags={selected.map((t) => ({ ...t, _inactive: !t.active }))} size="xs" max={max} />
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><TagIcon className="h-3.5 w-3.5" /> Add tags</span>
          )}
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" data-testid={`${testId}-panel`}>
        <div className="border-b border-border p-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tags…" className="h-8" data-testid={`${testId}-search`} />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {options.length === 0 && <p className="px-3 py-4 text-center text-xs text-muted-foreground">No tags.</p>}
          {options.map((t) => {
            const on = value.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-secondary"
                data-testid={`${testId}-option-${t.key || t.id}`}
              >
                <AccountTagText tag={t} inactive={!t.active} />
                {on && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TagPicker;
